import React, { useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Button, Icon } from 'semantic-ui-react';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import Security from '../../../utils/Security';
import { PROJECT_EXPORT } from '../../../queries/project';

const getStatusValue = (job) => {
  if (job.completedAt) return moment(job.completedAt).format('MM/DD/YYYY');
  return job.overall_status;
};

const buildExportData = (jobs) => {
  const shouldExportName = !Security.isDoubleBlinded();
  const shouldExportAdminFields = Security.isSuperUser() || Security.isDecibio();

  // Collect all unique qualification questions across all jobs (preserving first-seen order)
  const allQuestions = [
    ...new Set(
      jobs.flatMap((job) => (job.qualificationResponses || []).map((r) => r.question)),
    ),
  ];

  const baseHeaders = [
    'Date',
    ...(shouldExportAdminFields ? ['Email'] : []),
    ...(shouldExportName ? ['Name'] : []),
    'Title',
    'Country',
    'Specialty',
    'Organization',
    'Organization Type',
    ...(shouldExportAdminFields ? ['Hourly Rate', 'Paid?'] : []),
    'Meeting At',
  ];

  return [
    [...baseHeaders, ...allQuestions],
    ...jobs.map((job) => {
      const responseMap = Object.fromEntries(
        (job.qualificationResponses || []).map((r) => [
          r.question,
          r.response.join('; '),
        ]),
      );
      return [
        getStatusValue(job),
        ...(shouldExportAdminFields ? [job.user.email] : []),
        ...(shouldExportName ? [job.expert.fullName] : []),
        job.expert.title,
        job.expert.country,
        (job.expert.medical_specialties || []).join(', '),
        job.expert.organization.name,
        job.expert.organization.type,
        ...(shouldExportAdminFields ? [
          job.expert.hourlyRate ? `$${job.expert.hourlyRate}` : '$0',
          job.overall_status === 'Paid' ? 'Paid' : 'Not Paid',
        ] : []),
        job.meetingAt ? moment(job.meetingAt).format('LLLL') : 'N/A',
        ...allQuestions.map((q) => responseMap[q] ?? ''),
      ];
    }),
  ];
};

const ExportExpertButton = ({ projectId }) => {
  const csvLinkRef = useRef(null);
  const [fetchExportData, { data, loading }] = useLazyQuery(PROJECT_EXPORT, {
    variables: { id: projectId },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.project?.jobs) {
      csvLinkRef.current?.link?.click();
    }
  }, [data]);

  return (
    <>
      <Button
        className="basic-btn"
        loading={loading}
        onClick={() => fetchExportData()}
      >
        <Icon name="download" />
        Export
      </Button>
      {data?.project?.jobs && (
        <CSVLink
          ref={csvLinkRef}
          style={{ display: 'none' }}
          filename="experts.csv"
          data={buildExportData(data.project.jobs)}
        />
      )}
    </>
  );
};

export default ExportExpertButton;
